import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { FollowupVideoContext } from '@/types/api'
import { usePlanFeature } from '@/hooks/usePlanFeature'

/**
 * Videos de acompanhamento no contexto do aluno (spec 05).
 *
 * Arquivo proprio, separado de useAdminFollowupVideos.ts: la o assunto e o ciclo de vida
 * do arquivo (upload, encode, polling); aqui e QUANDO e ONDE o video aparece.
 *
 * Ver medhunter-api/docs/followup-videos.md.
 */

export const FOLLOWUP_RECOMMENDATIONS_KEY = ['followup-videos', 'recommendations'] as const
export const FOLLOWUP_PENDING_KEY = ['followup-videos', 'pending'] as const

/**
 * O casamento acontece no servidor e MATERIALIZA a oferta (grava a linha em
 * `user_followup_videos` que sustenta o banner e libera o playback). Por isso o cache
 * morno de 5 minutos importa: sem ele, cada render do plano do dia refaria a consulta.
 */
const RECOMMENDATIONS_STALE_MS = 1000 * 60 * 5

export interface FollowupRecommendationParams {
  /** Contexto `journey`: o topico do item da tarefa (`prefill.topic_id`). */
  topic_id?: number
  /** Contexto `manual`: os topicos do estudo que o aluno acabou de registrar. */
  topic_ids?: number[]
}

/**
 * `enabled` NAO e cortesia: o item de revisao e o de prova nao tem `topic_id`
 * (`prefill` e null), e pedir recomendacao sem payload devolveria sempre lista vazia
 * gastando uma request por linha do plano.
 */
export function useFollowupRecommendations(
  context: FollowupVideoContext,
  params: FollowupRecommendationParams = {},
  options: { enabled?: boolean } = {},
) {
  // r7-25: fora do plano a API responde 403; os paineis somem por nao ter dado.
  const inPlan = usePlanFeature('videos_acompanhamento')
  return useQuery({
    queryKey: [...FOLLOWUP_RECOMMENDATIONS_KEY, context, params],
    queryFn: () => api.followupVideos.recommendations(context, params),
    staleTime: RECOMMENDATIONS_STALE_MS,
    enabled: (options.enabled ?? true) && inPlan,
  })
}

/**
 * Os pendentes do banner.
 *
 * `staleTime: 0` de proposito, pelo mesmo motivo de `useInitialDataLoadState`: e o estado
 * que decide se o aviso aparece, e ele muda por acao do proprio aluno em outra tela. Um
 * cache morno faria o banner reaparecer depois de o video ter sido visto.
 */
export function useFollowupPending(enabled = true) {
  const inPlan = usePlanFeature('videos_acompanhamento')
  return useQuery({
    queryKey: FOLLOWUP_PENDING_KEY,
    queryFn: () => api.followupVideos.pending(),
    staleTime: 0,
    enabled: enabled && inPlan,
  })
}

/**
 * Invalida as duas chaves: marcar um video muda tanto a lista do contexto (onde `seen`
 * some) quanto o banner. Sem a segunda invalidacao o aviso continuaria de pe apontando
 * para um video que o aluno acabou de resolver.
 */
function useFollowupStateMutation(mutationFn: (id: number) => Promise<unknown>) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FOLLOWUP_RECOMMENDATIONS_KEY })
      void queryClient.invalidateQueries({ queryKey: FOLLOWUP_PENDING_KEY })
    },
  })
}

export function useMarkFollowupSeen() {
  return useFollowupStateMutation((id: number) => api.followupVideos.markSeen(id))
}

export function useSkipFollowupVideo() {
  return useFollowupStateMutation((id: number) => api.followupVideos.skip(id))
}
